/**
 * Series Timeline / Book Board helpers (#655, #668, #674, #700).
 * Cards live in projects.arc_structure. Book items are the shared
 * book objects Series Notes also reads — same id, not a title copy.
 * A book rename updates the linked card title on the server. Editing a
 * card here still does not rename the spawned book. Version 1 {books:[…]} and version 2
 * book|gap sequences migrate so old boards still show. Version 3 is
 * book | gap | entrance | exit | reveal.
 */

import { START_NOTE_LINE } from './writerCopy';

export const PROPOSITION_WORD_CAP = 50;
export const HOW_WORD_CAP = 50;
export const SHORT_LINE_WORD_CAP = 12;
export const BOARD_VERSION = 3;

export const ON_LINE_TYPES = ['book', 'gap'];
export const UNDER_LINE_TYPES = ['book'];
export const FREE_CARD_TYPES = ['entrance', 'exit', 'reveal'];
export const ABOVE_LINE_TYPES = FREE_CARD_TYPES;
export const ZOOM_MIN = 0.55;
export const ZOOM_MAX = 1.6;
export const ZOOM_STEP = 0.15;
export const GAP_DIRECTIONS = [
  { value: 'unspecified', label: 'Time direction unset' },
  { value: 'forward', label: 'Forward' },
  { value: 'backward', label: 'Backward' },
  { value: 'immediately_following', label: 'Immediately following' },
];
export const FREE_CARD_WIDTH = 268;
/** Compact rest-face estimate when layout has not measured yet. */
export const FREE_CARD_REST_HEIGHT = 96;
/**
 * Real pin with Done; taller than this is unsized jsdom, not a book card.
 * An open Reveal / Entrance measures ~226px in Chromium, so 220 threw the
 * real height away and clamped as if the card were 96px — the bottom of
 * the open card (Done) ran under the wire and was clipped by the sky.
 * Must stay below the sky + wire (260px) minus the string gap.
 */
export const FREE_CARD_MAX_HEIGHT = 244;
/** Gap between the bottom of a free card and the wire. */
export const FREE_CARD_STRING_GAP = 16;

function sanePinHeight(cardHeight) {
  if (!Number.isFinite(cardHeight) || cardHeight <= 0) return FREE_CARD_REST_HEIGHT;
  if (cardHeight > FREE_CARD_MAX_HEIGHT) return FREE_CARD_REST_HEIGHT;
  return cardHeight;
}

/**
 * Keep Entrance / Exit / Reveal in the sky. cork_y is the top of
 * the card; the whole card must stay above the line (#833 leftover
 * vs #813). A 72px ceiling on the top was enough to put the card
 * on and below the wire.
 */
export function clampFreeCardTop(corkY, lineY, cardHeight) {
  const height = sanePinHeight(cardHeight);
  const wire = Number.isFinite(lineY) && lineY >= 40 ? lineY : 248;
  const y = Number.isFinite(corkY) ? corkY : 0;
  const ceiling = Math.max(0, wire - height - FREE_CARD_STRING_GAP);
  return Math.max(0, Math.min(y, ceiling));
}

export const START_NOTE = START_NOTE_LINE;

export function wordCount(text) {
  const trimmed = (text || '').trim();
  if (!trimmed) return 0;
  return trimmed.split(/\s+/).length;
}

export function clampWords(text, cap) {
  const words = (text || '').trim().split(/\s+/).filter(Boolean);
  if (words.length <= cap) return (text || '');
  return words.slice(0, cap).join(' ');
}

export function clampProposition(text) {
  return clampWords(text, PROPOSITION_WORD_CAP);
}

export function clampHow(text) {
  return clampWords(text, HOW_WORD_CAP);
}

export function shortLine(text) {
  const words = (text || '').trim().split(/\s+/).filter(Boolean);
  if (!words.length) return '';
  if (words.length <= SHORT_LINE_WORD_CAP) return words.join(' ');
  return `${words.slice(0, SHORT_LINE_WORD_CAP).join(' ')}…`;
}

export function isFreeCard(item) {
  const kind = item?.type || 'book';
  return kind !== 'book' && kind !== 'gap';
}

export function isOnLine(item) {
  return !isFreeCard(item);
}

export function isGondola(item) {
  return (item?.type || 'book') === 'book';
}

export function isGapOnLine(item) {
  return item?.type === 'gap';
}

export function clampZoom(value) {
  const number = Number(value);
  if (!Number.isFinite(number)) return 1;
  const clamped = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, number));
  return Math.round(clamped * 100) / 100;
}

/** @deprecated use isFreeCard — Entrance / Exit float, they do not sit in a sky lane. */
export function isAboveLine(item) {
  return isFreeCard(item);
}

/** @deprecated use isOnLine — books and gaps stay on the railroad. */
export function isUnderLine(item) {
  return isOnLine(item);
}

export function lineItems(items) {
  return (items || []).filter(isOnLine);
}

export function freeItems(items) {
  return (items || []).filter(isFreeCard);
}

export function clampAttach(value) {
  const number = Number(value);
  if (!Number.isFinite(number)) return 0.5;
  return Math.min(1, Math.max(0, number));
}

function optionalNumber(value) {
  if (value === null || value === undefined || value === '') return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

export function defaultFreeLayout(items, item) {
  const list = items || [];
  const index = list.findIndex((entry) => entry.id === item?.id);
  const freeIndex = list.slice(0, Math.max(index, 0)).filter(isFreeCard).length;
  const attachFromIndex = index >= 0 && list.length
    ? clampAttach((index + 0.5) / list.length)
    : clampAttach(0.15 + freeIndex * 0.22);
  return {
    cork_x: 28 + freeIndex * 36,
    // Stay in the sky. Stacking Y used to walk later pins onto the
    // wire and over the gondola books (#833).
    cork_y: 24,
    attach: attachFromIndex,
  };
}

export function withFreeLayout(items) {
  return (items || []).map((item, index, list) => {
    if (!isFreeCard(item)) return item;
    const seeded = defaultFreeLayout(list, { ...item, id: item.id });
    const corkX = optionalNumber(item.cork_x);
    const corkY = optionalNumber(item.cork_y);
    const attach = optionalNumber(item.attach);
    return {
      ...item,
      cork_x: corkX === null ? seeded.cork_x : corkX,
      cork_y: corkY === null ? seeded.cork_y : corkY,
      attach: attach === null ? seeded.attach : clampAttach(attach),
    };
  });
}

export function placeFreeCard(items, card) {
  const seeded = defaultFreeLayout([...(items || []), card], card);
  return {
    ...card,
    cork_x: optionalNumber(card.cork_x) === null ? seeded.cork_x : card.cork_x,
    cork_y: optionalNumber(card.cork_y) === null ? seeded.cork_y : card.cork_y,
    attach: optionalNumber(card.attach) === null ? seeded.attach : clampAttach(card.attach),
  };
}

export function lineInsertIndex(items, lineIndex) {
  const list = items || [];
  let seen = 0;
  for (let i = 0; i < list.length; i += 1) {
    if (isOnLine(list[i])) {
      if (seen === lineIndex) return i;
      seen += 1;
    }
  }
  return list.length;
}

export function reorderLineItems(items, fromId, toId) {
  const list = items || [];
  const line = list.filter(isOnLine);
  const nextLine = reorderItems(line, fromId, toId);
  if (nextLine === line) return list;
  let index = 0;
  return list.map((item) => (isOnLine(item) ? nextLine[index++] : item));
}

function newId(_prefix) {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // PUT _card_id requires a UUID. The prefix fallback used to 400 the
  // first add in environments without randomUUID.
  const bytes = new Uint8Array(16);
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < 16; i += 1) bytes[i] = Math.floor(Math.random() * 256);
  }
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = [...bytes].map((b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export function emptyBoard() {
  return { version: BOARD_VERSION, items: [] };
}

export function bookItems(items) {
  return (items || []).filter((item) => (item.type || 'book') === 'book');
}

export function nextBookTitle(items) {
  const books = bookItems(items);
  const used = new Set();
  books.forEach((book) => {
    const match = /^(?:Book|book) (\d+)$/.exec((book.title || '').trim());
    if (match) used.add(Number(match[1]));
  });
  let n = books.length + 1;
  while (used.has(n)) n += 1;
  return `Book ${n}`;
}

export function newIdeaCard(items) {
  return {
    type: 'book',
    id: newId('card'),
    title: nextBookTitle(items),
    proposition: '',
    status: 'idea',
    story_id: null,
  };
}

export function newGapCard() {
  return {
    type: 'gap',
    id: newId('gap'),
    length: '',
    direction: 'forward',
    note: '',
  };
}

export function newEntranceCard() {
  return {
    type: 'entrance',
    id: newId('entrance'),
    character_name: '',
    how: '',
  };
}

export function newExitCard() {
  return {
    type: 'exit',
    id: newId('exit'),
    character_name: '',
    how: '',
  };
}

export function newRevealCard() {
  return {
    type: 'reveal',
    id: newId('reveal'),
    what: '',
    how: '',
  };
}

/**
 * One-time initialization when arc_structure is empty and stories already
 * exist. Not sync: later story changes do not call this again.
 */
export function seedBooksFromStories(stories) {
  return (stories || []).map((story, index) => ({
    type: 'book',
    id: (typeof crypto !== 'undefined' && crypto.randomUUID)
      ? crypto.randomUUID()
      : `seed-${story.id || index}`,
    title: (story.name || '').trim() || `Book ${index + 1}`,
    proposition: '',
    status: 'started',
    story_id: story.id,
  }));
}

export function insertItem(items, item, index) {
  const next = (items || []).slice();
  const at = Math.max(0, Math.min(index, next.length));
  next.splice(at, 0, item);
  return next;
}

export function reorderItems(items, fromId, toId) {
  const ids = items.map((item) => item.id);
  const from = ids.indexOf(fromId);
  const to = ids.indexOf(toId);
  if (from < 0 || to < 0 || from === to) return items;
  const next = items.slice();
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
}

/** @deprecated use reorderItems — same id-based move, books or gaps. */
export const reorderBooks = reorderItems;

export function markCardStarted(items, cardId, storyId) {
  return items.map((item) => (
    item.id === cardId
      ? { ...item, type: 'book', status: 'started', story_id: storyId }
      : item
  ));
}

function asBookItem(item) {
  return {
    type: 'book',
    id: item.id,
    title: item.title || '',
    proposition: item.proposition || '',
    status: item.status || 'idea',
    story_id: item.story_id || null,
  };
}

function asGapItem(item) {
  return {
    type: 'gap',
    id: item.id,
    length: item.length || '',
    direction: item.direction || 'unspecified',
    note: item.note || '',
  };
}

function asPinItem(item, type) {
  const pin = {
    type,
    id: item.id,
    how: item.how || '',
  };
  if (type === 'reveal') {
    pin.what = item.what || '';
  } else {
    pin.character_name = item.character_name || '';
  }
  const corkX = optionalNumber(item.cork_x);
  const corkY = optionalNumber(item.cork_y);
  const attach = optionalNumber(item.attach);
  if (corkX !== null) pin.cork_x = corkX;
  if (corkY !== null) pin.cork_y = corkY;
  if (attach !== null) pin.attach = clampAttach(attach);
  return pin;
}

function asTimelineItem(item) {
  const kind = item?.type || 'book';
  if (kind === 'gap') return asGapItem(item);
  if (kind === 'entrance' || kind === 'exit' || kind === 'reveal') {
    return asPinItem(item, kind);
  }
  return asBookItem(item);
}

export function migrateBoard(raw) {
  if (!raw || typeof raw !== 'object') return emptyBoard();
  if (Array.isArray(raw.items)) {
    return {
      version: BOARD_VERSION,
      items: raw.items.map(asTimelineItem),
    };
  }
  if (Array.isArray(raw.books)) {
    return {
      version: BOARD_VERSION,
      items: raw.books.map((book) => asBookItem(book)),
    };
  }
  return emptyBoard();
}

export function itemsFromBoard(raw) {
  return withFreeLayout(migrateBoard(raw).items);
}

export function boardPayload(items) {
  return { version: BOARD_VERSION, items };
}

/**
 * Field merge for one pin. Incoming wins when it sent the key.
 * Omitted / null incoming fields keep the value already on the pin
 * so a title save cannot blank what-this-book-is-about, Entrance how,
 * or a gap note.
 */
export function mergeItemFields(base, incoming) {
  if (!incoming) return base ? asTimelineItem(base) : incoming;
  if (!base) return asTimelineItem(incoming);
  const merged = { ...base, ...incoming };
  Object.keys(base).forEach((key) => {
    if (incoming[key] === undefined || incoming[key] === null) {
      merged[key] = base[key];
    }
  });
  return asTimelineItem(merged);
}

/**
 * Persist merge for the corkboard. Incoming order and fields win.
 * Pins that exist only on the base stay — a Book 1 blur must not drop
 * Book 3 or an Entrance the writer already placed.
 */
export function mergeTimelineItems(baseItems, incomingItems) {
  const base = Array.isArray(baseItems) ? baseItems : [];
  const incoming = Array.isArray(incomingItems) ? incomingItems : [];
  const baseById = new Map(
    base.filter((item) => item?.id).map((item) => [item.id, item]),
  );
  const seen = new Set();
  const merged = [];
  incoming.forEach((item) => {
    if (!item?.id || seen.has(item.id)) return;
    seen.add(item.id);
    merged.push(mergeItemFields(baseById.get(item.id), item));
  });
  base.forEach((item) => {
    if (!item?.id || seen.has(item.id)) return;
    seen.add(item.id);
    merged.push(asTimelineItem(item));
  });
  return merged;
}

export function isSeriesRoomPath(pathname) {
  return /^\/projects\/[^/]+\/series\/?$/.test(pathname || '');
}
